/**
 * Msg 565 · F.1 — Move Topic modal.
 *
 * First-class "Move to…" affordance for reparenting a topic. Uses the
 * D.4 topic search endpoint for the filter input, and lazy-loads
 * children via /topics/{slug}/children as the advisor expands the
 * tree. Cycle prevention is enforced client-side (self + descendants
 * are greyed out) AND server-side (400 → inline error).
 *
 * Kept intentionally small — react-refresh + visual-edits babel plugin
 * chokes on deeply recursive components in dev, so the tree renders as
 * a flat pre-computed list (same pattern as AtlasTopicTree.jsx).
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  X as CloseIcon, ChevronRight, ChevronDown, Loader2, Search,
  Move as MoveIcon, Home, Check,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { EntityIcon, humanize } from "./AtlasTopicShared";


// ─────────────────────────────────────────────────────────────
// Helper: BFS-collect every descendant slug of `slug`.
// ─────────────────────────────────────────────────────────────
async function collectDescendants(slug) {
  const out = new Set([slug]);
  const queue = [slug];
  const MAX = 500;
  while (queue.length > 0 && out.size < MAX) {
    const cur = queue.shift();
    try {
      const r = await api.get(`/atlas/topics/${cur}/children`);
      for (const k of (r.data?.children || [])) {
        if (!out.has(k.slug)) { out.add(k.slug); queue.push(k.slug); }
      }
    } catch { /* noop */ }
  }
  return out;
}


// ─────────────────────────────────────────────────────────────
// Move modal.
// ─────────────────────────────────────────────────────────────
export function MoveTopicModal({ open, topic, ancestors = [], onClose, onMoved, bulkSlugs = null }) {
  const [rootRows, setRootRows] = useState([]);
  const [openSet, setOpenSet] = useState(() => new Set());
  const [childCache, setChildCache] = useState({});
  const [loadingRoots, setLoadingRoots] = useState(false);
  const [loadingSlugs, setLoadingSlugs] = useState(() => new Set());
  const [selected, setSelected] = useState(null);   // slug of target parent, or "__ROOT__"
  const [q, setQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [blocked, setBlocked] = useState(() => new Set());
  const searchRef = useRef(null);

  // Bulk mode: `bulkSlugs` = array of slugs to move together. The
  // "current node" `topic` is passed for label + cycle prevention on
  // the first one; when in bulk mode we grey out the union of all
  // selected slugs' descendants (best-effort, first slug is enough
  // in practice for the common cruise-line case).
  const isBulk = Array.isArray(bulkSlugs) && bulkSlugs.length > 0;
  const bulkCount = isBulk ? bulkSlugs.length : 0;
  // Msg 602 · P0 fix — parent passes `bulkSlugs={Array.from(set)}` which
  // is a NEW array reference every render. Using it directly in the
  // reset-effect dep list re-fired the effect every render →
  // "Maximum update depth exceeded" storm. Stable key derived from
  // slug contents defeats that. Same trick for `ancestors`.
  const bulkKey = isBulk ? bulkSlugs.slice().sort().join(",") : "";
  const ancestorsKey = (ancestors || []).map((a) => a?.slug).join(",");

  // Reset when modal opens.
  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setQ(""); setSearchResults([]); setErr(null); setBusy(false);
    // F.3 — Pre-expand the ancestor chain so the tree opens showing
    // the current position, making sub-folder targets one hop away.
    const ancestorSlugs = (ancestors || []).map((a) => a.slug).filter(Boolean);
    setOpenSet(new Set(ancestorSlugs));
    setChildCache({});
    // Compute the cycle-prevention block set.
    const slug = topic?.slug;
    if (!slug && !isBulk) return;
    const targets = isBulk ? bulkSlugs : [slug];
    (async () => {
      const merged = new Set();
      for (const s of targets) {
        const kids = await collectDescendants(s);
        kids.forEach((k) => merged.add(k));
      }
      setBlocked(merged);
      // Pre-fetch children of every ancestor so they render expanded
      // without waiting on chevron clicks.
      const preload = ancestorSlugs.filter((s) => !merged.has(s));
      await Promise.all(preload.map(async (s) => {
        try {
          const r = await api.get(`/atlas/topics/${s}/children`);
          setChildCache((prev) => ({ ...prev, [s]: r.data?.children || [] }));
        } catch { /* noop */ }
      }));
    })();
    // Deps: `bulkKey`/`ancestorsKey` are content-stable strings.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, topic?.slug, isBulk, bulkKey, ancestorsKey]);

  // Load roots on open.
  useEffect(() => {
    if (!open) return;
    setLoadingRoots(true);
    api.get("/atlas/topics/tree?depth=1")
      .then((r) => setRootRows(r.data?.tree || []))
      .catch(() => setRootRows([]))
      .finally(() => setLoadingRoots(false));
  }, [open]);

  // Debounced search.
  useEffect(() => {
    if (!open) return;
    const qn = q.trim();
    if (qn.length < 2) { setSearchResults([]); setSearching(false); return; }
    let cancel = false;
    setSearching(true);
    const t = setTimeout(() => {
      api.get(`/atlas/topics/search?q=${encodeURIComponent(qn)}&limit=20`)
        .then((r) => { if (!cancel) setSearchResults(r.data?.results || []); })
        .catch(() => { if (!cancel) setSearchResults([]); })
        .finally(() => { if (!cancel) setSearching(false); });
    }, 250);
    return () => { cancel = true; clearTimeout(t); };
  }, [q, open]);

  const toggle = useCallback((s) => {
    const isOpen = openSet.has(s);
    if (isOpen) {
      const next = new Set(openSet); next.delete(s); setOpenSet(next);
      return;
    }
    // Open + load children if not cached.
    const next = new Set(openSet); next.add(s); setOpenSet(next);
    if (childCache[s] === undefined) {
      setLoadingSlugs((prev) => { const n = new Set(prev); n.add(s); return n; });
      api.get(`/atlas/topics/${s}/children`)
        .then((r) => setChildCache((prev) => ({ ...prev, [s]: r.data?.children || [] })))
        .catch(() => setChildCache((prev) => ({ ...prev, [s]: [] })))
        .finally(() => {
          setLoadingSlugs((prev) => { const n = new Set(prev); n.delete(s); return n; });
        });
    }
  }, [openSet, childCache]);

  // Flatten the tree for rendering.
  const flat = useMemo(() => {
    const out = [];
    const walk = (nodes, level) => {
      for (const n of nodes) {
        out.push({ node: n, level });
        if (openSet.has(n.slug)) {
          const kids = childCache[n.slug];
          if (Array.isArray(kids) && kids.length > 0) walk(kids, level + 1);
        }
      }
    };
    walk(rootRows.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")), 0);
    return out;
  }, [rootRows, openSet, childCache]);

  if (!open) return null;

  const currentSlug = topic?.slug || (isBulk ? bulkSlugs[0] : null);
  const currentPath = isBulk
    ? `${bulkCount} selected topics`
    : (ancestors.map((a) => a.name).concat([topic?.name || ""]).filter(Boolean).join(" / ") || (topic?.name || ""));
  const currentParent = topic?.parent_slugs?.[0] || null;

  const targetLabel = () => {
    if (selected === "__ROOT__") return "Make root topic (no parent)";
    if (!selected) return null;
    // Try to find name from rootRows or child cache or search.
    const findName = (slug) => {
      for (const r of rootRows) {
        if (r.slug === slug) return r.name;
        for (const [ , kids] of Object.entries(childCache)) {
          for (const k of (kids || [])) if (k.slug === slug) return k.name;
        }
      }
      for (const r of searchResults) if (r.slug === slug) return r.name;
      return slug;
    };
    return findName(selected);
  };

  const doMove = async () => {
    if (!selected) { setErr("Pick a new parent (or 'Make root topic') first."); return; }
    if (selected !== "__ROOT__" && blocked.has(selected)) {
      setErr("Can't move a topic under itself or one of its descendants.");
      return;
    }
    setBusy(true); setErr(null);
    try {
      const parentField = selected === "__ROOT__" ? "__ROOT__" : selected;
      const targets = isBulk ? bulkSlugs : [currentSlug];
      const results = await Promise.allSettled(targets.map((s) =>
        api.put(`/atlas/topics/${s}`, { parent_slug: parentField })
      ));
      const okCount = results.filter((r) => r.status === "fulfilled").length;
      const failCount = results.length - okCount;
      if (okCount === 0) {
        const first = results.find((r) => r.status === "rejected");
        const status = first?.reason?.response?.status;
        const detail = first?.reason?.response?.data?.detail;
        if (status === 400) setErr(formatApiErrorDetail(detail) || "Move rejected (cycle or invalid parent).");
        else setErr(formatApiErrorDetail(detail) || "Move failed.");
        setBusy(false);
        return;
      }
      if (failCount > 0) {
        toast.warning(`Moved ${okCount} · ${failCount} failed`);
      } else if (isBulk) {
        toast.success(`Moved ${okCount} topic${okCount === 1 ? "" : "s"}.`);
      } else {
        toast.success(`Moved to ${targetLabel() || "new parent"}.`);
      }
      onMoved?.({ movedSlugs: targets, newParent: parentField === "__ROOT__" ? null : parentField });
    } catch (ex) {
      const status = ex?.response?.status;
      const detail = ex?.response?.data?.detail;
      if (status === 400) setErr(formatApiErrorDetail(detail) || "Move rejected (cycle or invalid parent).");
      else setErr(formatApiErrorDetail(detail) || "Move failed.");
    } finally {
      setBusy(false);
    }
  };

  const showSearchResults = q.trim().length >= 2;

  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-start md:items-center justify-center overflow-y-auto p-3 md:p-4"
         data-testid="move-topic-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl border border-tb-navy/15 my-4 md:my-6 flex flex-col max-h-[90vh]">
        <MoveModalHeader isBulk={isBulk} bulkCount={bulkCount} name={topic?.name}
          onClose={onClose} />
        <div className="px-5 py-3 border-b border-tb-navy/10 space-y-2">
          <p className="font-hand text-[11px] text-tb-navy/60">
            Currently under: <b>{currentPath || "— root —"}</b>
          </p>
          <div className="relative">
            <Search className="w-4 h-4 text-tb-navy/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="move-topic-search"
              placeholder="Search for a parent topic by name or alias…"
              className="w-full h-10 pl-9 pr-3 rounded-full border border-tb-navy/20 bg-white outline-none font-hand text-sm text-tb-navy focus:border-tb-navy" />
            {searching && (
              <Loader2 className="w-4 h-4 text-tb-navy/40 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-2 min-h-[240px]">
          {showSearchResults ? (
            <MoveSearchResults
              results={searchResults}
              selected={selected}
              setSelected={setSelected}
              blocked={blocked}
              currentParent={currentParent}
            />
          ) : (
            <MoveTreeList
              flat={flat}
              loadingRoots={loadingRoots}
              loadingSlugs={loadingSlugs}
              openSet={openSet}
              onToggle={toggle}
              selected={selected}
              setSelected={setSelected}
              blocked={blocked}
              currentParent={currentParent}
            />
          )}
        </div>

        <MoveModalFooter
          err={err}
          selected={selected}
          selectedLabel={targetLabel()}
          busy={busy}
          onCancel={onClose}
          onConfirm={doMove}
        />
      </div>
    </div>
  );
}


function MoveModalHeader({ isBulk, bulkCount, name, onClose }) {
  return (
    <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3 border-b border-tb-navy/10">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <MoveIcon className="w-5 h-5 text-tb-navy" />
          <h2 className="text-tb-navy text-lg md:text-xl font-semibold truncate"
              style={{ fontFamily: '"Playfair Display", serif' }}
              data-testid="move-topic-modal-title">
            {isBulk
              ? `Move ${bulkCount} selected topic${bulkCount === 1 ? "" : "s"} to…`
              : `Move "${name || "topic"}" to a new parent`}
          </h2>
        </div>
      </div>
      <button type="button" onClick={onClose}
        data-testid="move-topic-close"
        className="h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-tb-navy/5 text-tb-navy/60">
        <CloseIcon className="w-4 h-4" />
      </button>
    </div>
  );
}


function MoveSearchResults({ results, selected, setSelected, blocked, currentParent }) {
  const filtered = results.filter((r) => !blocked.has(r.slug));
  if (results.length === 0) {
    return (
      <p className="font-hand text-tb-navy/50 text-xs mt-2 px-2">
        No matches. Try clearing the search to browse the tree.
      </p>
    );
  }
  return (
    <div data-testid="move-topic-search-results" className="space-y-1">
      <RootRow slug="__ROOT__" selected={selected} setSelected={setSelected} />
      {filtered.map((r) => (
        <MoveTargetRow key={r.slug} slug={r.slug} name={r.name}
          entityType={r.entity_type}
          categoryId={r.category_id}
          ancestorPath={r.ancestor_path}
          selected={selected} setSelected={setSelected}
          disabled={false}
          currentParent={currentParent}
        />
      ))}
      {results.length > filtered.length && (
        <p className="font-hand text-tb-navy/50 text-[11px] mt-2 px-2">
          {results.length - filtered.length} match{results.length - filtered.length === 1 ? " is" : "es are"} hidden (would create a cycle).
        </p>
      )}
    </div>
  );
}


function MoveTreeList({ flat, loadingRoots, loadingSlugs, openSet, onToggle,
                        selected, setSelected, blocked, currentParent }) {
  if (loadingRoots) {
    return (
      <div className="flex items-center gap-2 font-hand text-tb-navy/50 text-xs px-2 py-6">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading tree…
      </div>
    );
  }
  return (
    <div data-testid="move-topic-tree" className="space-y-0.5">
      <RootRow slug="__ROOT__" selected={selected} setSelected={setSelected} />
      {flat.map(({ node, level }) => {
        const isDisabled = blocked.has(node.slug);
        const isOpen = openSet.has(node.slug);
        const busy = loadingSlugs.has(node.slug);
        return (
          <div key={node.slug}
            className="flex items-stretch"
            style={{ paddingLeft: `${level * 14}px` }}
          >
            <button type="button"
              onClick={(e) => { e.stopPropagation(); onToggle(node.slug); }}
              data-testid={`move-topic-toggle-${node.slug}`}
              className="h-8 w-8 shrink-0 inline-flex items-center justify-center rounded hover:bg-tb-navy/5 text-tb-navy/70"
              aria-label={isOpen ? "Collapse" : "Expand"}
            >
              {busy
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : isOpen
                  ? <ChevronDown className="w-3.5 h-3.5" />
                  : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
            <div className="flex-1 min-w-0">
              <MoveTargetRow slug={node.slug} name={node.name}
                entityType={node.entity_type}
                categoryId={node.category_id}
                ancestorPath={null}
                selected={selected} setSelected={setSelected}
                disabled={isDisabled}
                currentParent={currentParent}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}


function RootRow({ slug, selected, setSelected }) {
  const active = selected === "__ROOT__";
  return (
    <button type="button"
      onClick={() => setSelected("__ROOT__")}
      data-testid="move-target-__ROOT__"
      aria-pressed={active}
      className={`w-full flex items-center gap-2 px-3 h-9 rounded-lg text-left border transition font-hand text-sm ${
        active
          ? "border-tb-navy bg-tb-navy/5 text-tb-navy"
          : "border-transparent hover:bg-tb-navy/[0.03] text-tb-navy/70"
      }`}
    >
      <span className={`h-4 w-4 rounded-full border shrink-0 inline-flex items-center justify-center ${
        active ? "border-tb-navy bg-tb-navy" : "border-tb-navy/30 bg-white"
      }`}>
        {active && <Check className="w-2.5 h-2.5 text-white" />}
      </span>
      <Home className="w-3.5 h-3.5 text-tb-navy/50" />
      <span className="font-semibold">Make root topic (no parent)</span>
    </button>
  );
}


function MoveTargetRow({ slug, name, entityType, categoryId = null, ancestorPath, selected, setSelected,
                         disabled, currentParent }) {
  const active = selected === slug && !disabled;
  const isCurrentParent = slug === currentParent;
  const cls = disabled
    ? "opacity-40 cursor-not-allowed border-transparent"
    : active
      ? "border-tb-navy bg-tb-navy/5 text-tb-navy cursor-pointer"
      : "border-transparent hover:bg-tb-navy/[0.03] text-tb-navy/85 cursor-pointer";
  const title = disabled
    ? "Can't move a topic under itself or one of its descendants."
    : isCurrentParent
      ? "This is the current parent."
      : "";
  return (
    <button type="button"
      onClick={() => { if (!disabled) setSelected(slug); }}
      disabled={disabled}
      data-testid={`move-target-${slug}`}
      aria-pressed={active}
      title={title}
      className={`w-full flex items-center gap-2 px-2.5 h-9 rounded-lg text-left border transition font-hand text-[13px] ${cls}`}
    >
      <span className={`h-4 w-4 rounded-full border shrink-0 inline-flex items-center justify-center ${
        active ? "border-tb-navy bg-tb-navy" : "border-tb-navy/30 bg-white"
      }`}>
        {active && <Check className="w-2.5 h-2.5 text-white" />}
      </span>
      <EntityIcon categoryId={categoryId} className="w-3.5 h-3.5 text-tb-navy/60" />
      <span className="truncate font-medium">{name}</span>
      {isCurrentParent && (
        <span className="ml-1 text-[9px] uppercase tracking-widest font-hand text-tb-navy/50 border border-tb-navy/20 rounded-full px-1.5">
          current
        </span>
      )}
      {ancestorPath && ancestorPath.length > 0 && (
        <span className="ml-auto shrink-0 text-[10px] font-hand text-tb-navy/40 truncate">
          {ancestorPath.map((a) => a.name).join(" / ")}
        </span>
      )}
    </button>
  );
}


function MoveModalFooter({ err, selected, selectedLabel, busy, onCancel, onConfirm }) {
  const disabled = busy || !selected;
  return (
    <div className="border-t border-tb-navy/10 px-5 py-4">
      {err && (
        <div data-testid="move-topic-error"
          className="mb-2 rounded-lg border border-red-300 bg-red-50 p-2.5 font-hand text-red-800 text-xs">
          {err}
        </div>
      )}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0 font-hand text-[11px] text-tb-navy/60">
          {selected
            ? <>Selected: <b className="text-tb-navy">{selectedLabel || selected}</b></>
            : "Pick a destination above."}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onCancel} disabled={busy}
            data-testid="move-topic-cancel"
            className="h-9 px-4 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-sm hover:bg-tb-navy/5">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={disabled}
            data-testid="move-topic-confirm"
            className="inline-flex items-center gap-2 h-9 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <MoveIcon className="w-4 h-4" />}
            Move here
          </button>
        </div>
      </div>
    </div>
  );
}
